import React, { useState, useEffect } from 'react';
import { onGameEvent } from '../../core/GameEvents';
import { networkManager } from '../../systems/NetworkManager';
import type { BountyInfo } from '@astra/shared';
import { SHIPS, BOUNTY_CONFIG } from '@astra/shared';
import {
  PALETTE,
  stationContainerStyle,
  StationHeader,
  StationFooter,
  Hint,
  getTierColor,
} from './stationTheme';

// Format credits with commas
function formatCredits(amount: number): string {
  return amount.toLocaleString();
}

// Format countdown timer as M:SS
function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// Get ship name from class ID
function getShipName(shipClassId: string): string {
  const shipDef = SHIPS[shipClassId];
  return shipDef?.name || 'Unknown Ship';
}

// Get ship class type name
function getShipClassName(shipClassId: string): string {
  const shipDef = SHIPS[shipClassId];
  if (!shipDef) return 'Unknown';
  const shipClass = shipDef.class || 'unknown';
  return shipClass.charAt(0).toUpperCase() + shipClass.slice(1);
}

interface BountiesProps {
  stationId: string;
  onBack: () => void;
}

export function Bounties({ stationId, onBack }: BountiesProps) {
  const [bounties, setBounties] = useState<BountyInfo[]>([]);
  const [lastUpdated, setLastUpdated] = useState<number>(Date.now());
  const [countdownText, setCountdownText] = useState<string>('5:00');

  // Request bounty list when component mounts
  useEffect(() => {
    networkManager.requestBountyList();
  }, []);

  // Listen for bounty list updates
  useEffect(() => {
    const unsubscribeList = onGameEvent('bountyListUpdate', (data) => {
      setBounties(data.bounties);
      setLastUpdated(data.timestamp);
    });

    const unsubscribeCleared = onGameEvent('bountyCleared', (data) => {
      setBounties((prev) => prev.filter((b) => b.playerId !== data.playerId));
    });

    return () => {
      unsubscribeList();
      unsubscribeCleared();
    };
  }, []);

  // Update countdown timer every second
  useEffect(() => {
    const updateCountdown = () => {
      const elapsed = Date.now() - lastUpdated;
      const remaining = BOUNTY_CONFIG.BROADCAST_INTERVAL_MS - elapsed;
      setCountdownText(formatCountdown(Math.max(0, remaining)));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  return (
    <div style={stationContainerStyle}>
      <StationHeader onBack={onBack} title="BOUNTY BOARD" hint="[ESC] Return to Station" />

      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {/* Subtitle bar */}
        <div style={{
          padding: '12px 16px',
          marginBottom: 16,
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderRadius: 4,
          border: `1px solid ${PALETTE.BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ color: PALETTE.RED, fontSize: 14, letterSpacing: '0.05em' }}>WANTED CRIMINALS</span>
          <span style={{ color: PALETTE.TEAL, fontSize: 11, marginLeft: 'auto' }}>
            Next broadcast in {countdownText}
          </span>
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bounties.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: PALETTE.DIM,
              padding: '40px 20px',
            }}>
              <p style={{ fontSize: 14, marginBottom: 8 }}>No active bounties at this time.</p>
              <p style={{ fontSize: 12 }}>The constellation is peaceful... for now.</p>
            </div>
          ) : (
            bounties.map((bounty) => (
              <div key={bounty.playerId} style={{
                backgroundColor: PALETTE.PANEL,
                border: `1px solid ${PALETTE.BORDER}`,
                borderRadius: 4,
                padding: '12px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 600, color: PALETTE.RED }}>{bounty.playerName}</span>
                </div>
                <div style={{ fontSize: 12, color: PALETTE.DIM, lineHeight: 1.6 }}>
                  <div>
                    <span style={{ color: PALETTE.DIM }}>Last seen: </span>
                    <span style={{ color: PALETTE.LIGHT }}>{bounty.lastConstellationName}</span>
                  </div>
                  <div>
                    <span style={{ color: PALETTE.DIM }}>Ship: </span>
                    <span style={{ color: getTierColor(bounty.lastShipTier) }}>
                      {getShipName(bounty.lastShipClass)}
                    </span>
                    <span style={{ color: PALETTE.DIM }}> (T{bounty.lastShipTier} {getShipClassName(bounty.lastShipClass)})</span>
                  </div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: PALETTE.YELLOW, marginTop: 8 }}>
                  Bounty: {formatCredits(bounty.amount)} CR
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <StationFooter>Active bounties in this constellation</StationFooter>
    </div>
  );
}

export default Bounties;
